import { v4 as uuidv4 } from 'uuid';
import app from './appwx'; // 假设这是你的云函数调用模块
import db from './database'; // 假设这是你的数据库模块
import axios from 'axios';

// 初始化设备ID
async function initializeDeviceId() {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
}

// 带重试功能的fetch请求
async function fetchWithRetry(url, retries = 3, timeout = 5000) {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await axios.get(url, { timeout });
      return response.data;
    } catch (error) {
      if (i === retries - 1) {
        throw error;
      }
      // console.warn(`Attempt ${i + 1} failed: ${error.message}`);
    }
  }
}

// 获取用户的IP地址
async function getUserIP() {
  const service = 'https://ipinfo.io/json';
  try {
    const result = await fetchWithRetry(service);
    // console.log("ip:",result)
    return result.ip;
  } catch (error) {
    // console.error('获取IP地址失败:', error);
    throw new Error('获取IP地址失败');
  }
}

// IPv4地址转换为数字
function ipToLong(ip) {
  return ip.split('.').reduce((acc, octet) => {
    return (acc << 8) + parseInt(octet, 10);
  }, 0);
}

// 检查IPv4是否在指定范围内
function isIPv4InRange(ip, range) {
  const [start, end] = range.includes('-') ? range.split('-').map(ipToLong) : [ipToLong(range), ipToLong(range)];
  const ipNum = ipToLong(ip);
  return ipNum >= start && ipNum <= end;
}

// IPv6地址转换为大整数
function ipv6ToBigInt(ip) {
  const fullIp = expandIPv6(ip);
  if (!isValidIPv6(fullIp)) {
    throw new Error(`无效的IPv6地址: ${fullIp}`);
  }
  return BigInt('0x' + fullIp.split(':').map(part => part.padStart(4, '0')).join(''));
}

// 检查IPv6地址是否在指定范围内
function isIPv6InRange(ip, range) {
  if (!range) {
    return false;
  }

  const [start, prefix] = range.split('/');
  if (!prefix || prefix.length === 0) {
    throw new Error(`无效的IPv6范围: ${range}`);
  }

  const prefixLen = parseInt(prefix, 10);
  const mask = BigInt('0xffffffffffffffffffffffffffffffff') << BigInt(128 - prefixLen);

  const ipNum = ipv6ToBigInt(ip);
  const startNum = ipv6ToBigInt(start);

  return (ipNum & mask) === (startNum & mask);
}

// 验证IPv6地址
function isValidIPv6(ip) {
  const fullIp = expandIPv6(ip);
  return /^([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}$/i.test(fullIp);
}

// 扩展IPv6地址
function expandIPv6(address) {
  const parts = address.split(':');
  let expanded = [];
  let skipIndex = parts.indexOf('');

  // 处理前导或尾随的 '::'
  if (skipIndex === 0) {
    expanded = Array(8).fill('0000');
  } else if (skipIndex === parts.length - 1) {
    expanded = parts.map(part => part.padStart(4, '0'));
    expanded = Array(8 - expanded.length).fill('0000').concat(expanded);
  } else {
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === '') {
        while (expanded.length < 8 - (parts.length - i - 1)) {
          expanded.push('0000');
        }
      } else {
        expanded.push(parts[i].padStart(4, '0'));
      }
    }
  }

  return expanded.join(':');
}

// GPS定位
async function getGPSLocation(deviceInfo) {
  try {
    const deviceId = await initializeDeviceId();
    deviceInfo.device_id = deviceId;

    if (!navigator.geolocation) {
      // console.error('Geolocation is not supported by your browser');
      return null;
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          deviceInfo.gpsinfo = {
            latitude,
            longitude,
            accuracy: 150,
            altitude: position.coords.altitude || 0,
            bearing: position.coords.heading || 0,
            speed: position.coords.speed || 0,
            viewstar: 0, // 视情况填写真实数据
            usedstar: 0  // 视情况填写真实数据
          };

          const result = await callCloudFunction('getGPS', deviceInfo);

          const response = JSON.parse(result.result.body);

          if (response.status === 0) {
            const location = {
              ...response.result,
              method: 'GPS'
            };
            // console.log('腾讯地图定位成功:', location);

            const { data: schools } = await db.collection('schoolGPS').get();

            const userPoint = [location.location.latitude, location.location.longitude]; // 修改这里，确保获取正确的纬度和经度

            // 遍历所有学校，检查用户点是否在任何一个学校的多边形范围内
            for (const school of schools) {
              const coordinates = school.coordinates.map(coord => coord.split(',').map(Number));
              if (isPointInPolygon(userPoint, coordinates)) {
                location.foundSchool = school.role;
                break;
              }
            }

            if (!location.foundSchool) {
              location.foundSchool = "其他";
            }

            resolve(location); // 确保 resolve location
          } else {
            // console.error('腾讯地图定位失败:', response.message);
            resolve(null);
          }
        },
        async (error) => {
          // console.error('腾讯地图定位请求失败:', error);
          resolve(null);
        }
      );
    });
  } catch (error) {
    // console.error('腾讯地图定位请求失败:', error);
    return null;
  }
}

// 调用云函数
async function callCloudFunction(name, data) {
  try {
    const result = await app.callFunction({
      name: name,
      data: data
    });
    return result;
  } catch (error) {
    // console.error('调用云函数失败:', error);
    throw new Error('Network Error');
  }
}

// 检查点是否在多边形内
function isPointInPolygon(point, polygon) {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// 检查用户位置
async function checkUserLocation() {
  try {
    const deviceInfo = { key: "XC2BZ-B6CWV-H7QPT-5BEN7-F7KH2-WOBVM" };
    const ip = await getUserIP();

    const schoolIPs = await db.collection('schoolIP').get().then(result => result.data);
    // console.log(`ipip: ip`,schoolIPs);

    let foundSchool = schoolIPs.find(school => {
      const ipv4Match = school.ipv4Ranges.split(', ').some(range => isIPv4InRange(ip, range.trim()));
      return ipv4Match;
    });

    if (foundSchool) {
      // console.log(`IPv4匹配到的学校: ${foundSchool.role}`);
      return { ip, foundSchool: foundSchool.role };
    } else {
      // console.log('IPv4定位未匹配到学校，尝试IPv6定位');
      const ipv6 = await getUserIP(); // 获取IPv6地址

      foundSchool = schoolIPs.find(school => {
        const ipv6Match = school.ipv6Ranges.split(', ').some(range => {
          try {
            return range && isIPv6InRange(ipv6, range.trim());
          } catch (error) {
            // console.error(`检查IPv6范围时出错: ${error.message}`);
            return false;
          }
        });
        return ipv6Match;
      });

      if (foundSchool) {
        // console.log(`IPv6匹配到的学校: ${foundSchool.role}`);
        return { ipv6, foundSchool: foundSchool.role };
      } else {
        // console.log('IPv6定位未匹配到学校，尝试使用GPS定位');
        const location = await getGPSLocation(deviceInfo);
        if (location && location.foundSchool) {
          // console.log(`GPS定位匹配到的学校: ${location.foundSchool}`);
        } else {
          location.foundSchool = "其他";
          // console.log('GPS定位未匹配到学校，显示“其他”');
        }

        // location.foundSchool = "学校";

        return location;
      }
    }
  } catch (error) {
    // console.error("获取位置或学校数据失败:", error);
    throw error;
  }
}

// 调用函数检查用户位置并返回结果
checkUserLocation().then(location => {
  // console.log("最终定位信息:", location);
  localStorage.setItem('location', JSON.stringify(location));
}).catch(error => {
  // console.error("检查用户位置时出错:", error);
});

export { checkUserLocation };



// WEBPACK FOOTER //
// ./src/api/GPS.js